/**
 * Team Manager Management Section
 * Handles Team Manager assignment dropdown with invite and delete functionality
 */
import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { UserCircle, Send, Trash2, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/api';

export default function ManagerManagementSection({
  managers,
  currentManagerId,
  currentManagerName,
  childId,
  isViewOnly,
  onManagerChange,
  onManagersRefresh
}) {
  const [assigningManager, setAssigningManager] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newManagerData, setNewManagerData] = useState({ name: '', email: '' });
  const [addingManager, setAddingManager] = useState(false);

  const assignManager = async (managerId) => {
    if (!childId || isViewOnly) return;
    setAssigningManager(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (managerId) {
        const response = await axios.post(`${API}/children/${childId}/assign-team-manager`, {
          team_manager_id: managerId
        }, { headers });
        
        toast.success(`Team Manager assigned: ${response.data.team_manager_name}`);
        if (onManagerChange) {
          onManagerChange({
            id: managerId,
            name: response.data.team_manager_name
          });
        }
      } else {
        await axios.post(`${API}/children/${childId}/unassign-team-manager`, {}, { headers });
        toast.success('Team Manager unassigned');
        if (onManagerChange) {
          onManagerChange({ id: '', name: '' });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Team Manager assignment');
    } finally {
      setAssigningManager(false);
    }
  };

  const handleAddManager = async (e) => {
    e.preventDefault();
    if (!newManagerData.name || !newManagerData.email) {
      toast.error('Please fill in name and email');
      return;
    }
    
    setAddingManager(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/auth/invite-team-manager`, {
        name: newManagerData.name,
        email: newManagerData.email
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success('Invitation sent to Team Manager!');
      setShowAddModal(false);
      setNewManagerData({ name: '', email: '' });
      if (onManagersRefresh) onManagersRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setAddingManager(false);
    }
  };

  const handleDeleteManager = async (managerId, managerName) => {
    if (!window.confirm(`Are you sure you want to remove ${managerName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/users/${managerId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success('Team Manager removed');
      if (onManagersRefresh) onManagersRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete manager');
    }
  };

  const [resendingInvite, setResendingInvite] = useState(false);
  const handleResendInvite = async () => {
    if (!currentManagerId) return;
    
    setResendingInvite(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/resend-team-manager-invite`, {
        manager_id: currentManagerId
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Invitation resent to Team Manager!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingInvite(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs text-zinc-400 flex items-center gap-1">
        <UserCircle className="h-3 w-3" /> Team Manager Assignment
      </label>
      
      {/* Manager dropdown with invite button */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <select
            value={currentManagerId}
            onChange={(e) => assignManager(e.target.value)}
            disabled={assigningManager || isViewOnly}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#02b5ff] outline-none disabled:opacity-50"
            data-testid="manager-select"
          >
            <option value="">Unassigned</option>
            {managers.map((manager, i) => (
              <option key={i} value={manager.id}>
                {manager.full_name}
              </option>
            ))}
          </select>
          {assigningManager && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-[#02b5ff]" />
            </div>
          )}
        </div>
        
        {!isViewOnly && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowAddModal(true)}
            className="border-zinc-700 text-zinc-400 hover:text-white px-2"
            title="Invite New Team Manager"
          >
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Show assigned manager with resend invite option */}
      {!isViewOnly && currentManagerName && (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded flex items-center gap-1">
            {currentManagerName}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleResendInvite}
            disabled={resendingInvite}
            className="h-6 px-2 text-xs text-[#02b5ff] hover:text-[#02b5ff] hover:bg-[#02b5ff]/10"
            title="Resend invitation email"
          >
            {resendingInvite ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <>
                <Send className="h-3 w-3 mr-1" />
                Resend Invite
              </>
            )}
          </Button>
          <button
            onClick={() => handleDeleteManager(currentManagerId, currentManagerName)}
            className="text-zinc-600 hover:text-red-400 p-1"
            title="Remove manager"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Invite Manager Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Invite Team Manager</h3>
              <button onClick={() => setShowAddModal(false)} className="text-zinc-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-sm text-zinc-400 mb-4">
              An email invitation will be sent with login instructions.
            </p>
            
            <form onSubmit={handleAddManager} className="space-y-4">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Full Name</label>
                <input
                  type="text"
                  value={newManagerData.name}
                  onChange={(e) => setNewManagerData({ ...newManagerData, name: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Email</label>
                <input
                  type="email"
                  value={newManagerData.email}
                  onChange={(e) => setNewManagerData({ ...newManagerData, email: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
                  placeholder="jane@example.com"
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={addingManager}
                  className="flex-1 bg-[#02b5ff] text-white"
                >
                  {addingManager ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <>
                      <Send className="h-4 w-4 mr-1" /> Send Invite
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
